/**
 * The definition of the page routes.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

export enum AppRouteUrl {
  ApiKeyCreate = '/api-keys/create',
  ApiKey = '/api-keys',
  ChangePassword = '/change-password',
  ConfirmAccount = '/confirm',
  EmailConfirmation = '/resend-activation-link',
  ForgotPassword = '/forgot-password',
  Home = '/',
  LogIn = '/login',
  MyAccount = '/my-account',
  NewPasswordConfirmation = '/forgot-password-confirmation',
  Settings = '/settings',
  SignUp = '/signup',
  SubscriptionSelection = '/tierselection',
  TaskCreation = '/job-creation',
  TaskHistory = '/jobs',
  Team = '/team',
  ThankYou = '/thank-you',
  Usage = '/usage',
  WebhookCreate = '/webhook/create',
  WebhookTest = '/webhook/:webhookId/test',
}

const AppRouteTitleList: Record<string, string> = {
  [AppRouteUrl.ApiKeyCreate]: 'Create API Credentials',
  [AppRouteUrl.ApiKey]: 'API Keys',
  [AppRouteUrl.ChangePassword]: 'Change password',
  [AppRouteUrl.ConfirmAccount]: 'Confirm Account',
  [AppRouteUrl.EmailConfirmation]: 'E-mail Confirmation',
  [AppRouteUrl.ForgotPassword]: 'Forgot Password',
  [AppRouteUrl.Home]: 'Home Page',
  [AppRouteUrl.MyAccount]: 'My account',
  [AppRouteUrl.NewPasswordConfirmation]: 'New Password Confirmation',
  [AppRouteUrl.LogIn]: 'Log in',
  [AppRouteUrl.Settings]: 'Settings',
  [AppRouteUrl.SignUp]: 'Sign up',
  [AppRouteUrl.SubscriptionSelection]: 'Subscription Selection',
  [AppRouteUrl.TaskCreation]: 'Create a new Task',
  [AppRouteUrl.TaskHistory]: 'Tasks History',
  [AppRouteUrl.Team]: 'Team',
  [AppRouteUrl.ThankYou]: 'Thank you',
  [AppRouteUrl.Usage]: 'Usage',
  [AppRouteUrl.WebhookCreate]: 'Create a Webhook',
  [AppRouteUrl.WebhookTest]: 'Webhook Test',
}

/**
 * Return title of route.
 * @param route The requested route.
 * @returns The message or null if not found.
 */
export function getRouteTitle(route?: AppRouteUrl | string): string | null {
  if (!route) {
    return null
  }

  if (route in AppRouteTitleList) {
    return AppRouteTitleList[route]
  }

  // Route not found: try to convert routes with arguments like `{0}`
  // into a RegEx pattern.
  const match = Object.entries(AppRouteTitleList).find(([pattern]) =>
    patternToRegExp(pattern).test(route)
  )

  return match ? match[1] : null
}

/**
 * Turn a route template such as `/webhooks/{0}/logs` into a matching
 * regular expression, where each `{n}` placeholder matches one path
 * segment.
 */
function patternToRegExp(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.*+?^$()|[\]\\{}]/g, '\\$&')
    .replace(/\\\{\d+\\\}/g, '[^/]+')

  return new RegExp(`^${escaped}$`)
}
